import { useNavigate } from '@tanstack/react-router';
import {
  type ChangeEvent,
  type ElementRef,
  type HTMLProps,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { type LBIcon, Icons } from '@/assets/icons/icons';
import { Panel } from '@/stores/ui.store';
import { classList } from '@/utils/cn';

const routes: Record<string, { icon: LBIcon; label: string; panel: Panel }> = {
  '/': { icon: Icons.home, label: 'Home', panel: Panel.Home },
  '/chats': { icon: Icons.chat, label: 'Chats', panel: Panel.Chats },
  '/routines': {
    icon: Icons.routines,
    label: 'Routines',
    panel: Panel.Routines,
  },
  '/meetings': {
    icon: Icons.meetings,
    label: 'Meetings',
    panel: Panel.Meetings,
  },
  '/journal': { icon: Icons.journal, label: 'Journals', panel: Panel.Journal },
  '/ingestion': {
    icon: Icons.ingestion,
    label: 'Ingestion',
    panel: Panel.Ingestion,
  },
};

export function PanelNavigationTitle() {
  const navigate = useNavigate();
  const measureTitleRef = useRef<ElementRef<'span'>>(null);
  const selectRef = useRef<ElementRef<'select'>>(null);

  const handleOnChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const path = event.target.value;
    const route = routes[path];
    if (route) navigate({ to: path });
  };

  const handleOnContextMenu = (event: ReactMouseEvent<HTMLSelectElement>) => {
    event.preventDefault();

    if (selectRef.current) {
      const element = selectRef.current;

      element.focus();

      const clickEvent = new MouseEvent('mousedown');

      element.dispatchEvent(clickEvent);
    }
  };

  useEffect(() => {
    if (measureTitleRef.current && selectRef.current) {
      const { width } = measureTitleRef.current.getBoundingClientRect();

      selectRef.current.style.setProperty('--select-width', `${width + 8}px`);
    }
  }, [selectRef, measureTitleRef]);

  const Icon = useMemo(() => {
    const BlankIcon = (_: HTMLProps<SVGElement>) => <></>;
    const path = window.location.pathname;
    const route = Object.entries(routes).find(([key]) => path.startsWith(key));
    return route ? routes[route[0]].icon : BlankIcon;
  }, []);

  const currentPath = window.location.pathname;
  const currentRoute = Object.entries(routes).find(([key]) =>
    currentPath.startsWith(key)
  );
  const currentLabel = currentRoute ? routes[currentRoute[0]].label : '';

  return (
    <div className="relative m-auto flex items-center justify-center gap-2">
      <span
        ref={measureTitleRef}
        className="absolute touch-none pointer-events-none whitespace-nowrap opacity-0"
      >
        {currentLabel}
      </span>

      <Icon className="text-md transform-gpu h-4 w-4" />

      <select
        ref={selectRef}
        className={classList(
          'appearance-none bg-transparent border-none',
          'focus:outline-none focus:ring-0'
        )}
        name="page"
        value={currentPath}
        onChange={handleOnChange}
        onContextMenu={handleOnContextMenu}
      >
        {Object.entries(routes).map(([path, { label }]) => (
          <option key={path} value={path}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}
